import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus, RefreshCw, Calendar, ChevronDown, ChevronUp,
  Edit3, HardDrive, Image, Search, AlertTriangle, CheckCircle2, Clock, X, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import HardDrivesTab from '../components/HardDrivesTab';

const API_BASE = '/vault';
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const BRANDS = [
  { value: 'wittyshadows', label: 'Wittyshadows' },
  { value: 'trio_bliss', label: 'Trio Bliss' }
];

const DataManagementPage = () => {
  const { user } = useAuth();
  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  const [activeTab, setActiveTab] = useState(isManager ? 'projects' : 'my-events');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [freelancers, setFreelancers] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});

  // Dialogs
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDeliverable, setShowDeliverable] = useState(false);
  const [showScreenshots, setShowScreenshots] = useState(false);
  const [showCardEntry, setShowCardEntry] = useState(false);

  // Form data
  const [editProject, setEditProject] = useState(null);
  const [deliverableProject, setDeliverableProject] = useState(null);
  const [screenshotEvent, setScreenshotEvent] = useState(null);
  const [cardEntryData, setCardEntryData] = useState(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    client_name: '', event_date: '', brand: 'wittyshadows', num_events: 1,
    events: [{ name: 'Event 1', team_members: [], freelancers: [] }]
  });

  // Reports
  const [summaryReport, setSummaryReport] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [myEvents, setMyEvents] = useState([]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/projects?year=${year}`;
      if (month) url += `&month=${month}`;
      const res = await api.get(url);
      setProjects(res.data);
    } catch (err) {
      toast.error('Failed to load vault projects');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  const fetchTeamAndFreelancers = useCallback(async () => {
    try {
      const [teamRes, flRes] = await Promise.all([
        api.get(`${API_BASE}/team-members`),
        api.get(`${API_BASE}/freelancer-list`)
      ]);
      setTeamMembers(teamRes.data);
      setFreelancers(flRes.data);
    } catch (err) {
      console.error('Failed to fetch team/freelancers');
    }
  }, []);

  const fetchSummaryReport = useCallback(async () => {
    try {
      let url = `${API_BASE}/reports/summary?year=${year}`;
      if (month) url += `&month=${month}`;
      const res = await api.get(url);
      setSummaryReport(res.data);
    } catch (err) {
      console.error('Failed to fetch summary report');
    }
  }, [year, month]);

  const fetchTeamPerformance = useCallback(async () => {
    try {
      let url = `${API_BASE}/reports/team-performance?year=${year}`;
      if (month) url += `&month=${month}`;
      const res = await api.get(url);
      setTeamPerformance(res.data);
    } catch (err) {
      console.error('Failed to fetch team performance');
    }
  }, [year, month]);

  const fetchMyEvents = useCallback(async () => {
    try {
      const res = await api.get(`${API_BASE}/my-events`);
      setMyEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch my events');
    }
  }, []);

  useEffect(() => {
    fetchTeamAndFreelancers();
  }, [fetchTeamAndFreelancers]);

  useEffect(() => {
    if (activeTab === 'projects') fetchProjects();
    else if (activeTab === 'summary') fetchSummaryReport();
    else if (activeTab === 'performance') fetchTeamPerformance();
    else if (activeTab === 'my-events') fetchMyEvents();
  }, [activeTab, fetchProjects, fetchSummaryReport, fetchTeamPerformance, fetchMyEvents]);

  // ==================== HANDLERS ====================

  const handleCreateProject = async () => {
    if (!createForm.client_name || !createForm.event_date) {
      toast.error('Client name and event date are required');
      return;
    }
    try {
      await api.post(`${API_BASE}/projects`, {
        client_name: createForm.client_name,
        event_date: createForm.event_date,
        brand: createForm.brand,
        events: createForm.events.map(e => ({
          name: e.name,
          team_members: e.team_members,
          freelancers: e.freelancers
        }))
      });
      toast.success('Project created successfully');
      setShowCreate(false);
      setCreateForm({ client_name: '', event_date: '', brand: 'wittyshadows', num_events: 1, events: [{ name: 'Event 1', team_members: [], freelancers: [] }] });
      fetchProjects();
    } catch (err) {
      toast.error('Failed to create project');
    }
  };

  const handleUpdateProject = async () => {
    if (!editProject) return;
    try {
      await api.put(`${API_BASE}/projects/${editProject.id}`, {
        client_name: editProject.client_name,
        event_date: editProject.event_date,
        brand: editProject.brand,
        events: editProject.events.map(e => ({
          id: e.id,
          name: e.name,
          status: e.status,
          team_members: e.team_members.map(m => typeof m === 'string' ? m : m.user_id),
          freelancers: e.freelancers.map(m => typeof m === 'string' ? m : m.user_id)
        }))
      });
      toast.success('Project updated');
      setShowEdit(false);
      setEditProject(null);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to update project');
    }
  };

  const handleUpdateDeliverable = async () => {
    if (!deliverableProject) return;
    try {
      await api.put(`${API_BASE}/projects/${deliverableProject.id}/deliverable`, deliverableProject.deliverable);
      toast.success('Deliverable updated');
      setShowDeliverable(false);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to update deliverable');
    }
  };

  const handleUpdateCards = async (projectId, eventId, userId, cardsTotal, cardsUploaded, memberType = 'team_members') => {
    try {
      await api.patch(`${API_BASE}/projects/${projectId}/events/${eventId}/cards`, {
        user_id: userId,
        cards_total: cardsTotal,
        cards_uploaded: cardsUploaded,
        member_type: memberType
      });
      toast.success('Cards updated');
      if (activeTab === 'my-events') fetchMyEvents();
      else fetchProjects();
    } catch (err) {
      toast.error('Failed to update cards');
    }
  };

  const handleToggleEventStatus = async (projectId, eventId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'complete' ? 'pending' : 'complete';
      await api.patch(`${API_BASE}/projects/${projectId}/events/${eventId}/status`, { status: newStatus });
      toast.success(`Event marked as ${newStatus}`);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to update event status');
    }
  };

  const handleScreenshotUpload = async (projectId, eventId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`${API_BASE}/projects/${projectId}/events/${eventId}/screenshots`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Screenshot uploaded');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to upload screenshot');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its data?')) return;
    try {
      await api.delete(`${API_BASE}/projects/${projectId}`);
      toast.success('Project deleted');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const updateCreateFormEvents = (count) => {
    const n = Math.max(1, Math.min(20, count));
    const events = [...createForm.events];
    while (events.length < n) events.push({ name: `Event ${events.length + 1}`, team_members: [], freelancers: [] });
    while (events.length > n) events.pop();
    setCreateForm({ ...createForm, num_events: n, events });
  };

  const toggleExpandProject = (id) => {
    setExpandedProjects(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  // ==================== TABS ====================

  const tabs = isManager
    ? [
      { key: 'projects', label: 'Projects' },
      { key: 'summary', label: 'Summary Report' },
      { key: 'performance', label: 'Team Performance' },
      { key: 'drives', label: 'Hard Drives' },
    ]
    : [
      { key: 'my-events', label: 'My Assigned Events' },
      { key: 'drives', label: 'Hard Drives' },
    ];

  // ==================== RENDER ====================

  if (loading && activeTab === 'projects') {
    return (
      <div className="flex items-center justify-center py-20" data-testid="vault-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Data Management</h1>
          <p className="text-zinc-500 text-sm mt-1">Studio data tracking & card management</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 pb-0">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.key
                ? 'text-[#F5A623] border-[#F5A623]'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <ProjectsTab
          projects={projects}
          year={year}
          setYear={setYear}
          month={month}
          setMonth={setMonth}
          isManager={isManager}
          expandedProjects={expandedProjects}
          toggleExpandProject={toggleExpandProject}
          formatDate={formatDate}
          onRefresh={fetchProjects}
          onCreate={() => setShowCreate(true)}
          onEdit={(p) => { setEditProject(JSON.parse(JSON.stringify(p))); setShowEdit(true); }}
          onDeliverable={(p) => { setDeliverableProject(JSON.parse(JSON.stringify(p))); setShowDeliverable(true); }}
          onViewScreenshots={(p, e) => { setScreenshotEvent({ project: p, event: e }); setShowScreenshots(true); }}
          onToggleEventStatus={handleToggleEventStatus}
          onDelete={handleDeleteProject}
          onCardEntry={(p, e, m, type) => { setCardEntryData({ project: p, event: e, member: m, type }); setShowCardEntry(true); }}
          onScreenshotUpload={handleScreenshotUpload}
        />
      )}

      {/* Summary Report Tab */}
      {activeTab === 'summary' && (
        <SummaryReportTab
          data={summaryReport}
          year={year}
          setYear={setYear}
          month={month}
          setMonth={setMonth}
          onRefresh={fetchSummaryReport}
        />
      )}

      {/* Team Performance Tab */}
      {activeTab === 'performance' && (
        <TeamPerformanceTab
          data={teamPerformance}
          year={year}
          setYear={setYear}
          month={month}
          setMonth={setMonth}
          onRefresh={fetchTeamPerformance}
        />
      )}

      {/* My Events Tab (Team Member) */}
      {activeTab === 'my-events' && (
        <MyEventsTab
          events={myEvents}
          onRefresh={fetchMyEvents}
          onUpdateCards={handleUpdateCards}
        />
      )}

      {/* Hard Drives Tab */}
      {activeTab === 'drives' && (
        <HardDrivesTab isManager={isManager} />
      )}

      {/* ====== DIALOGS ====== */}

      {/* Create Project Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Client Name</Label>
                <Input value={createForm.client_name} onChange={e => setCreateForm({...createForm, client_name: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Client name" data-testid="vault-client-name" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Event Date</Label>
                <Input type="date" value={createForm.event_date} onChange={e => setCreateForm({...createForm, event_date: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="vault-event-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Brand</Label>
                <Select value={createForm.brand} onValueChange={v => setCreateForm({...createForm, brand: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="vault-brand-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    {BRANDS.map(b => <SelectItem key={b.value} value={b.value} className="text-white hover:bg-white/10">{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Number of Events</Label>
                <Input type="number" min={1} max={20} value={createForm.num_events}
                  onChange={e => updateCreateFormEvents(parseInt(e.target.value) || 1)}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="vault-num-events" />
              </div>
            </div>

            <div className="border-t border-white/10 pt-4">
              <h3 className="text-sm font-medium text-zinc-300 mb-3">Events & Teams</h3>
              {createForm.events.map((ev, idx) => (
                <EventFormBlock
                  key={idx}
                  index={idx}
                  event={ev}
                  teamMembers={teamMembers}
                  freelancers={freelancers}
                  onChange={(updated) => {
                    const newEvents = [...createForm.events];
                    newEvents[idx] = updated;
                    setCreateForm({...createForm, events: newEvents});
                  }}
                />
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleCreateProject} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="vault-create-btn">Create Project</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
            <p className="text-zinc-500 text-xs mt-1">Update team assignments for events</p>
          </DialogHeader>
          {editProject && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Client Name</Label>
                  <Input value={editProject.client_name} onChange={e => setEditProject({...editProject, client_name: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">Event Date</Label>
                  <Input value={editProject.event_date} readOnly className="bg-[#0A0A0A] border-white/10 text-zinc-500 mt-1" />
                </div>
              </div>

              <div className="border-t border-white/10 pt-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-3">Events & Teams</h3>
                {editProject.events.map((ev, idx) => (
                  <EditEventBlock
                    key={ev.id || idx}
                    event={ev}
                    teamMembers={teamMembers}
                    freelancers={freelancers}
                    onChange={(updated) => {
                      const newEvents = [...editProject.events];
                      newEvents[idx] = updated;
                      setEditProject({...editProject, events: newEvents});
                    }}
                  />
                ))}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEdit(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleUpdateProject} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="vault-update-btn">Update Project</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliverable Dialog */}
      <Dialog open={showDeliverable} onOpenChange={setShowDeliverable}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Client Deliverable</DialogTitle>
            {deliverableProject && <p className="text-zinc-500 text-sm">{deliverableProject.client_name}</p>}
          </DialogHeader>
          {deliverableProject && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Client Drive Details</Label>
                <Textarea value={deliverableProject.deliverable?.client_drive_details || ''}
                  onChange={e => setDeliverableProject({...deliverableProject, deliverable: {...deliverableProject.deliverable, client_drive_details: e.target.value}})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" rows={3} data-testid="vault-drive-details" />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={deliverableProject.deliverable?.data_saved || false}
                    onCheckedChange={v => setDeliverableProject({...deliverableProject, deliverable: {...deliverableProject.deliverable, data_saved: v}})} />
                  <span className="text-sm text-zinc-300">Data Saved</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={deliverableProject.deliverable?.drive_delivered || false}
                    onCheckedChange={v => setDeliverableProject({...deliverableProject, deliverable: {...deliverableProject.deliverable, drive_delivered: v}})} />
                  <span className="text-sm text-zinc-300">Drive Delivered</span>
                </label>
              </div>

              <div className="border-t border-white/10 pt-4">
                <h4 className="text-sm font-medium text-zinc-300 mb-2">Archive</h4>
                <div>
                  <Label className="text-zinc-400 text-xs uppercase">External Drive #</Label>
                  <Input value={deliverableProject.deliverable?.external_drive_number || ''}
                    onChange={e => setDeliverableProject({...deliverableProject, deliverable: {...deliverableProject.deliverable, external_drive_number: e.target.value}})}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="EXT-001" data-testid="vault-drive-number" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-3">
                  <Checkbox checked={deliverableProject.deliverable?.data_removed || false}
                    onCheckedChange={v => setDeliverableProject({...deliverableProject, deliverable: {...deliverableProject.deliverable, data_removed: v}})} />
                  <span className="text-sm text-zinc-300">Data Removed from System</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowDeliverable(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleUpdateDeliverable} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="vault-save-deliverable">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Screenshots Dialog */}
      <Dialog open={showScreenshots} onOpenChange={setShowScreenshots}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Event Screenshots</DialogTitle>
            {screenshotEvent && <p className="text-zinc-500 text-sm">{screenshotEvent.event.name}</p>}
          </DialogHeader>
          {screenshotEvent && (
            <div className="space-y-4 mt-4">
              {screenshotEvent.event.screenshots?.length > 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  {screenshotEvent.event.screenshots.map((s, i) => (
                    <div key={i} className="border border-white/10 rounded overflow-hidden">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${s.url}`} alt={`Screenshot ${i+1}`}
                        className="w-full h-32 object-cover" />
                      <div className="p-2 text-xs text-zinc-500">
                        {s.uploaded_by} - {new Date(s.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-center py-4">No screenshots yet</p>
              )}
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Upload Screenshot</Label>
                <Input type="file" accept="image/*" className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                  onChange={e => {
                    if (e.target.files[0]) {
                      handleScreenshotUpload(screenshotEvent.project.id, screenshotEvent.event.id, e.target.files[0]);
                    }
                  }} data-testid="vault-screenshot-upload" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Card Entry Dialog */}
      <Dialog open={showCardEntry} onOpenChange={setShowCardEntry}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Card Numbers</DialogTitle>
            {cardEntryData && <p className="text-zinc-500 text-sm">{cardEntryData.member.name} - {cardEntryData.event.name}</p>}
          </DialogHeader>
          {cardEntryData && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Total Cards</Label>
                <Input type="number" min={0} value={cardEntryData.member.cards_total}
                  onChange={e => setCardEntryData({...cardEntryData, member: {...cardEntryData.member, cards_total: parseInt(e.target.value) || 0}})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="vault-cards-total" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase">Cards Uploaded</Label>
                <Input type="number" min={0} value={cardEntryData.member.cards_uploaded}
                  onChange={e => setCardEntryData({...cardEntryData, member: {...cardEntryData.member, cards_uploaded: parseInt(e.target.value) || 0}})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="vault-cards-uploaded" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowCardEntry(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="vault-save-cards"
                  onClick={() => {
                    handleUpdateCards(cardEntryData.project.id, cardEntryData.event.id, cardEntryData.member.user_id, cardEntryData.member.cards_total, cardEntryData.member.cards_uploaded, cardEntryData.type);
                    setShowCardEntry(false);
                  }}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== SUB-COMPONENTS ====================

const YearMonthFilter = ({ year, setYear, month, setMonth, onRefresh }) => (
  <div className="flex items-center gap-3 flex-wrap">
    <div className="flex items-center gap-2">
      <span className="text-zinc-500 text-sm">Year:</span>
      <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
        <SelectTrigger className="w-24 bg-[#0A0A0A] border-white/10 text-white h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#121212] border-white/10">
          {[2024, 2025, 2026, 2027].map(y => (
            <SelectItem key={y} value={String(y)} className="text-white hover:bg-white/10">{y}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="flex items-center gap-1">
      <button onClick={() => setMonth(null)}
        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${!month ? 'bg-[#F5A623] text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}
        data-testid="filter-all-months">All</button>
      {MONTHS.map((m, i) => (
        <button key={m} onClick={() => setMonth(i + 1)}
          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${month === i + 1 ? 'bg-[#F5A623] text-black' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}
          data-testid={`filter-month-${i+1}`}>{m}</button>
      ))}
    </div>
    <Button variant="outline" size="sm" onClick={onRefresh} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10 ml-auto" data-testid="vault-refresh">
      <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh Data
    </Button>
  </div>
);

const EventFormBlock = ({ index, event, teamMembers, freelancers, onChange }) => {
  const [tab, setTab] = useState('team');

  const toggleMember = (id, listKey) => {
    const current = event[listKey] || [];
    const updated = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
    onChange({ ...event, [listKey]: updated });
  };

  return (
    <Card className="bg-[#0A0A0A] border-white/10 mb-3">
      <CardContent className="p-4">
        <p className="text-sm font-medium text-zinc-300 mb-2">Event {index + 1}</p>
        <Input value={event.name} onChange={e => onChange({ ...event, name: e.target.value })}
          className="bg-[#121212] border-white/10 text-white mb-3" placeholder="Event name"
          data-testid={`vault-event-name-${index}`} />
        <div className="flex gap-2 mb-2">
          <button onClick={() => setTab('team')}
            className={`px-3 py-1 text-xs rounded font-medium ${tab === 'team' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>Team</button>
          <button onClick={() => setTab('freelancers')}
            className={`px-3 py-1 text-xs rounded font-medium ${tab === 'freelancers' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>Freelancers</button>
        </div>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {tab === 'team' && teamMembers.map(m => (
            <label key={m.id} className="flex items-center gap-2 cursor-pointer py-1">
              <Checkbox checked={event.team_members.includes(m.id)}
                onCheckedChange={() => toggleMember(m.id, 'team_members')} />
              <span className="text-sm text-zinc-300">{m.name}</span>
            </label>
          ))}
          {tab === 'freelancers' && freelancers.map(f => (
            <label key={f.id} className="flex items-center gap-2 cursor-pointer py-1">
              <Checkbox checked={event.freelancers.includes(f.id)}
                onCheckedChange={() => toggleMember(f.id, 'freelancers')} />
              <span className="text-sm text-zinc-300">{f.name} <span className="text-zinc-600 text-xs">({f.speciality})</span></span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const EditEventBlock = ({ event, teamMembers, freelancers, onChange }) => {
  const [tab, setTab] = useState('team');
  const currentTeamIds = (event.team_members || []).map(m => typeof m === 'string' ? m : m.user_id);
  const currentFlIds = (event.freelancers || []).map(m => typeof m === 'string' ? m : m.user_id);

  const toggleMember = (id, listKey) => {
    const currentIds = listKey === 'team_members' ? currentTeamIds : currentFlIds;
    const updated = currentIds.includes(id) ? currentIds.filter(x => x !== id) : [...currentIds, id];
    onChange({ ...event, [listKey]: updated });
  };

  return (
    <Card className="bg-[#0A0A0A] border-white/10 mb-3">
      <CardContent className="p-4">
        <p className="text-sm font-medium text-zinc-300 mb-2">{event.name}</p>
        <Input value={event.name} onChange={e => onChange({ ...event, name: e.target.value })}
          className="bg-[#121212] border-white/10 text-white mb-3" />
        <div className="flex gap-2 mb-2">
          <button onClick={() => setTab('team')}
            className={`px-3 py-1 text-xs rounded font-medium ${tab === 'team' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>Team</button>
          <button onClick={() => setTab('freelancers')}
            className={`px-3 py-1 text-xs rounded font-medium ${tab === 'freelancers' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>Freelancers</button>
        </div>
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {tab === 'team' && teamMembers.map(m => (
            <label key={m.id} className="flex items-center gap-2 cursor-pointer py-1">
              <Checkbox checked={currentTeamIds.includes(m.id)}
                onCheckedChange={() => toggleMember(m.id, 'team_members')} />
              <span className="text-sm text-zinc-300">{m.name}</span>
            </label>
          ))}
          {tab === 'freelancers' && freelancers.map(f => (
            <label key={f.id} className="flex items-center gap-2 cursor-pointer py-1">
              <Checkbox checked={currentFlIds.includes(f.id)}
                onCheckedChange={() => toggleMember(f.id, 'freelancers')} />
              <span className="text-sm text-zinc-300">{f.name}</span>
            </label>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== PROJECTS TAB ====================

const ProjectsTab = ({ projects, year, setYear, month, setMonth, isManager, expandedProjects, toggleExpandProject, formatDate, onRefresh, onCreate, onEdit, onDeliverable, onViewScreenshots, onToggleEventStatus, onDelete, onCardEntry, onScreenshotUpload }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3 flex-wrap">
      <YearMonthFilter year={year} setYear={setYear} month={month} setMonth={setMonth} onRefresh={onRefresh} />
      {isManager && (
        <Button onClick={onCreate} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="vault-new-project">
          <Plus className="h-4 w-4 mr-1.5" /> Create Project
        </Button>
      )}
    </div>

    {projects.length === 0 ? (
      <div className="text-center py-16 text-zinc-600">
        <HardDrive className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>No projects found for this period</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {projects.map(project => (
          <ProjectCard
            key={project.id}
            project={project}
            expanded={expandedProjects[project.id]}
            toggleExpand={() => toggleExpandProject(project.id)}
            formatDate={formatDate}
            isManager={isManager}
            onEdit={onEdit}
            onDeliverable={onDeliverable}
            onViewScreenshots={onViewScreenshots}
            onToggleEventStatus={onToggleEventStatus}
            onDelete={onDelete}
            onCardEntry={onCardEntry}
            onScreenshotUpload={onScreenshotUpload}
          />
        ))}
      </div>
    )}
  </div>
);

const ProjectCard = ({ project, expanded, toggleExpand, formatDate, isManager, onEdit, onDeliverable, onViewScreenshots, onToggleEventStatus, onDelete, onCardEntry }) => {
  const events = project.events || [];
  const visibleEvents = expanded ? events : events.slice(0, 3);
  const hasMore = events.length > 3;

  return (
    <Card className="bg-[#121212] border-white/10" data-testid={`vault-project-${project.id}`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h3 className="text-white font-semibold text-base">{project.client_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-zinc-500 text-sm">{formatDate(project.event_date)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs bg-white/5 text-zinc-400 border-white/10">{events.length} Events</Badge>
            {project.brand === 'trio_bliss' && (
              <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20">Trio Bliss</Badge>
            )}
          </div>
        </div>

        {/* Events */}
        <div className="mt-4 space-y-3">
          {visibleEvents.map(event => (
            <EventRow
              key={event.id}
              event={event}
              project={project}
              isManager={isManager}
              onViewScreenshots={onViewScreenshots}
              onToggleEventStatus={onToggleEventStatus}
              onCardEntry={onCardEntry}
            />
          ))}
        </div>

        {hasMore && !expanded && (
          <button onClick={toggleExpand} className="text-[#F5A623] text-sm mt-3 hover:underline" data-testid="vault-expand-events">
            + {events.length - 3} more events (click to expand)
          </button>
        )}
        {hasMore && expanded && (
          <button onClick={toggleExpand} className="text-zinc-500 text-sm mt-3 hover:underline">
            Collapse
          </button>
        )}

        {/* Action Buttons */}
        {isManager && (
          <div className="flex gap-2 mt-4 pt-3 border-t border-white/5">
            <Button variant="outline" size="sm" onClick={() => onEdit(project)} className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5" data-testid="vault-edit-btn">
              <Edit3 className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDeliverable(project)} className="flex-1 border-white/10 text-zinc-300 hover:bg-white/5" data-testid="vault-deliverable-btn">
              <HardDrive className="h-3.5 w-3.5 mr-1.5" /> Deliverable
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EventRow = ({ event, project, isManager, onViewScreenshots, onToggleEventStatus, onCardEntry }) => {
  const allMembers = [...(event.team_members || []), ...(event.freelancers || [])];
  const totalCards = allMembers.reduce((sum, m) => sum + (m.cards_total || 0), 0);
  const uploadedCards = allMembers.reduce((sum, m) => sum + (m.cards_uploaded || 0), 0);

  return (
    <div className={`p-3 rounded border ${event.status === 'complete' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-white/5 bg-white/[0.02]'}`}
      data-testid={`vault-event-${event.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white text-sm font-medium">{event.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">Cards: {uploadedCards}/{totalCards}</span>
          {isManager ? (
            <button onClick={() => onToggleEventStatus(project.id, event.id, event.status)}
              data-testid={`vault-toggle-status-${event.id}`}>
              <Badge variant="outline" className={`text-xs cursor-pointer ${
                event.status === 'complete'
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
              }`}>
                {event.status === 'complete' ? <><CheckCircle2 className="h-3 w-3 mr-1" />Complete</> : <><Clock className="h-3 w-3 mr-1" />Pending</>}
              </Badge>
            </button>
          ) : (
            <Badge variant="outline" className={`text-xs ${
              event.status === 'complete' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
            }`}>
              {event.status === 'complete' ? 'Complete' : 'Pending'}
            </Badge>
          )}
        </div>
      </div>

      {/* Members */}
      <div className="space-y-1">
        {(event.team_members || []).map(m => (
          <div key={m.user_id} className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">{m.name}</span>
            <button onClick={() => onCardEntry(project, event, m, 'team_members')}
              className="text-zinc-500 hover:text-[#F5A623] transition-colors cursor-pointer"
              data-testid={`vault-card-entry-${m.user_id}`}>
              {m.cards_total > 0 ? `${m.cards_uploaded}/${m.cards_total} cards` : 'Set cards'}
            </button>
          </div>
        ))}
        {(event.freelancers || []).map(m => (
          <div key={m.user_id} className="flex items-center justify-between text-xs">
            <span className="text-purple-400">{m.name} <span className="text-zinc-600">(FL)</span></span>
            <button onClick={() => onCardEntry(project, event, m, 'freelancers')}
              className="text-zinc-500 hover:text-[#F5A623] transition-colors cursor-pointer">
              {m.cards_total > 0 ? `${m.cards_uploaded}/${m.cards_total} cards` : 'Set cards'}
            </button>
          </div>
        ))}
      </div>

      {/* Screenshots link */}
      {(event.screenshots?.length > 0) && (
        <button onClick={() => onViewScreenshots(project, event)} className="text-[#F5A623] text-xs mt-2 flex items-center gap-1 hover:underline"
          data-testid={`vault-view-screenshots-${event.id}`}>
          <Image className="h-3 w-3" /> View {event.screenshots.length} Screenshot{event.screenshots.length > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
};

// ==================== SUMMARY REPORT TAB ====================

const SummaryReportTab = ({ data, year, setYear, month, setMonth, onRefresh }) => (
  <div className="space-y-4">
    <YearMonthFilter year={year} setYear={setYear} month={month} setMonth={setMonth} onRefresh={onRefresh} />

    <h2 className="text-xl font-semibold text-white">Summary Report</h2>

    {data.length === 0 ? (
      <p className="text-zinc-500 text-center py-8">No data for this period</p>
    ) : (
      <div className="space-y-4">
        {data.map(item => (
          <SummaryCard key={item.id} item={item} />
        ))}
      </div>
    )}
  </div>
);

const SummaryCard = ({ item }) => {
  const allComplete = item.status === 'complete';

  return (
    <Card className="bg-[#121212] border-white/10 border-l-4 border-l-[#F5A623]/40" data-testid={`summary-card-${item.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold">{item.client_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-zinc-500 text-sm">{item.event_date}</span>
              {item.brand === 'trio_bliss' && (
                <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-400 border-purple-500/20">Trio Bliss</Badge>
              )}
            </div>
          </div>
          <Badge variant="outline" className={`${allComplete ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
            {allComplete ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Complete</> : `${item.total_events - item.completed_events} Pending`}
          </Badge>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-[#0A0A0A] rounded p-3 border border-white/5">
            <p className="text-xs text-zinc-500">Events</p>
            <p className="text-lg font-bold text-white">{item.completed_events}/{item.total_events}</p>
            <p className="text-xs text-zinc-600">Completed</p>
          </div>
          <div className="bg-[#0A0A0A] rounded p-3 border border-white/5">
            <p className="text-xs text-zinc-500">Cards</p>
            <p className="text-lg font-bold text-white">{item.uploaded_cards}/{item.total_cards}</p>
            <p className="text-xs text-zinc-600">Uploaded</p>
          </div>
          <div className="bg-[#0A0A0A] rounded p-3 border border-white/5">
            <p className="text-xs text-zinc-500">Missing Data</p>
            <p className={`text-lg font-bold ${item.missing_cards > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{item.missing_cards}</p>
            <p className="text-xs text-zinc-600">Cards Pending</p>
          </div>
          <div className="bg-[#0A0A0A] rounded p-3 border border-white/5">
            <p className="text-xs text-zinc-500">Status</p>
            <p className={`text-sm font-bold mt-1 ${allComplete ? 'text-emerald-400' : 'text-rose-400'}`}>
              {allComplete ? 'Complete' : <><AlertTriangle className="h-3.5 w-3.5 inline mr-1" />Incomplete</>}
            </p>
          </div>
        </div>

        {/* Pending events detail */}
        {item.pending_events.length > 0 && (
          <div className="bg-amber-500/5 border border-amber-500/10 rounded p-3">
            <p className="text-xs font-medium text-amber-400 mb-2">Pending Events:</p>
            {item.pending_events.map((pe, i) => (
              <div key={i} className="mb-2 last:mb-0">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-zinc-300">{pe.event_name}</span>
                  <span className="text-xs text-zinc-500">{pe.cards_info} cards</span>
                </div>
                {pe.pending_members.map((pm, j) => (
                  <div key={j} className="flex justify-between items-center ml-4 text-xs mt-0.5">
                    <span className={pm.not_assigned ? 'text-amber-400' : 'text-zinc-500'}>{pm.name}</span>
                    <span className={pm.not_assigned ? 'text-amber-400' : 'text-rose-400'}>
                      {pm.not_assigned ? '(team not yet assigned)' : `${pm.cards_pending} cards pending`}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== TEAM PERFORMANCE TAB ====================

const TeamPerformanceTab = ({ data, year, setYear, month, setMonth, onRefresh }) => (
  <div className="space-y-4">
    <YearMonthFilter year={year} setYear={setYear} month={month} setMonth={setMonth} onRefresh={onRefresh} />

    <h2 className="text-xl font-semibold text-white">Team Performance Report</h2>

    {data.length === 0 ? (
      <p className="text-zinc-500 text-center py-8">No performance data for this period</p>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.map(member => (
          <TeamMemberCard key={member.user_id} member={member} />
        ))}
      </div>
    )}
  </div>
);

const TeamMemberCard = ({ member }) => (
  <Card className="bg-[#121212] border-white/10 border-l-4 border-l-[#F5A623]/40" data-testid={`perf-card-${member.user_id}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">{member.name}</h3>
          <span className="text-xs text-zinc-500">{member.type}</span>
        </div>
        {member.pending_events > 0 && (
          <Badge variant="outline" className="bg-rose-500/10 text-rose-400 border-rose-500/20">
            {member.pending_events} Pending
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-[#0A0A0A] rounded p-2.5 border border-white/5 text-center">
          <p className="text-xs text-zinc-500">Total Events</p>
          <p className="text-lg font-bold text-white">{member.total_events}</p>
        </div>
        <div className="bg-[#0A0A0A] rounded p-2.5 border border-emerald-500/10 text-center">
          <p className="text-xs text-emerald-500">Completed</p>
          <p className="text-lg font-bold text-emerald-400">{member.completed_events}</p>
        </div>
        <div className="bg-[#0A0A0A] rounded p-2.5 border border-rose-500/10 text-center">
          <p className="text-xs text-rose-500">Pending</p>
          <p className="text-lg font-bold text-rose-400">{member.pending_events}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-[#0A0A0A] rounded p-2.5 border border-white/5">
          <p className="text-xs text-emerald-500">Cards Uploaded</p>
          <p className="text-lg font-bold text-emerald-400">{member.cards_uploaded}</p>
        </div>
        <div className="bg-[#0A0A0A] rounded p-2.5 border border-white/5">
          <p className="text-xs text-rose-500">Cards Pending</p>
          <p className="text-lg font-bold text-rose-400">{member.cards_pending}</p>
        </div>
      </div>

      {member.pending_events_detail.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/10 rounded p-3 mt-2">
          <p className="text-xs font-medium text-amber-400 mb-1.5">Pending Events Summary:</p>
          {member.pending_events_detail.map((d, i) => (
            <div key={i} className="flex justify-between items-center text-xs py-0.5">
              <span className="text-zinc-400">{d.project_name} - {d.event_name}</span>
              {d.cards_pending > 0 && <span className="text-rose-400">{d.cards_pending} cards</span>}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

// ==================== MY EVENTS TAB (Team Member View) ====================

const MyEventsTab = ({ events, onRefresh, onUpdateCards }) => {
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [cardEdit, setCardEdit] = useState(null);

  const pending = events.filter(e => e.status === 'pending');
  const completed = events.filter(e => e.status === 'complete');
  const displayed = (tab === 'pending' ? pending : completed).filter(e =>
    e.client_name.toLowerCase().includes(search.toLowerCase()) || e.event_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">My Assigned Events</h2>
        <Button variant="outline" size="sm" onClick={onRefresh} className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="vault-refresh-my-events">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh Data
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search card..." className="pl-10 bg-[#121212] border-white/10 text-white" data-testid="vault-search-events" />
        </div>
        <div className="flex gap-1">
          <button onClick={() => setTab('pending')}
            className={`px-3 py-1.5 text-sm rounded font-medium ${tab === 'pending' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid="vault-pending-tab">Pending ({pending.length})</button>
          <button onClick={() => setTab('completed')}
            className={`px-3 py-1.5 text-sm rounded font-medium ${tab === 'completed' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid="vault-completed-tab">Completed ({completed.length})</button>
        </div>
      </div>

      {displayed.length === 0 ? (
        <div className="text-center py-16 text-zinc-600">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>{tab === 'pending' ? 'No pending events' : 'No completed events'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Group by project */}
          {Object.entries(
            displayed.reduce((acc, ev) => {
              const key = `${ev.project_id}_${ev.client_name}`;
              if (!acc[key]) acc[key] = { client_name: ev.client_name, event_date: ev.event_date, events: [] };
              acc[key].events.push(ev);
              return acc;
            }, {})
          ).map(([key, group]) => (
            <Card key={key} className="bg-[#121212] border-white/10 border-l-4 border-l-amber-500/40">
              <CardContent className="p-5">
                <h3 className="text-white font-semibold">{group.client_name}</h3>
                <p className="text-xs text-zinc-500 mb-3">{group.event_date}</p>

                {group.events.map(ev => (
                  <div key={ev.event_id} className={`p-3 rounded border mb-2 last:mb-0 ${
                    ev.status === 'complete' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'
                  }`} data-testid={`my-event-${ev.event_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm font-medium">{ev.event_name}</span>
                      <Badge variant="outline" className={`text-xs ${
                        ev.status === 'complete' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      }`}>
                        {ev.status === 'complete' ? 'Complete' : 'Pending'}
                      </Badge>
                    </div>

                    {ev.cards_total === 0 ? (
                      <div className="mt-2">
                        <p className="text-xs text-amber-400 mb-1">Card numbers not set</p>
                        {cardEdit?.event_id === ev.event_id ? (
                          <div className="flex items-center gap-2">
                            <Input type="number" min={0} value={cardEdit.total} onChange={e => setCardEdit({...cardEdit, total: parseInt(e.target.value) || 0})}
                              className="bg-[#0A0A0A] border-white/10 text-white h-8 w-24 text-sm" placeholder="Total" data-testid="my-event-cards-total" />
                            <Input type="number" min={0} value={cardEdit.uploaded} onChange={e => setCardEdit({...cardEdit, uploaded: parseInt(e.target.value) || 0})}
                              className="bg-[#0A0A0A] border-white/10 text-white h-8 w-24 text-sm" placeholder="Uploaded" data-testid="my-event-cards-uploaded" />
                            <Button size="sm" className="bg-[#F5A623] text-black h-8" data-testid="my-event-save-cards"
                              onClick={() => { onUpdateCards(ev.project_id, ev.event_id, null, cardEdit.total, cardEdit.uploaded); setCardEdit(null); }}>
                              Save
                            </Button>
                            <Button size="sm" variant="outline" className="border-white/10 text-zinc-400 h-8" onClick={() => setCardEdit(null)}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 h-7 text-xs"
                            onClick={() => setCardEdit({ event_id: ev.event_id, total: 0, uploaded: 0 })} data-testid="my-event-set-cards">
                            Set Card Numbers
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-zinc-500">Cards: {ev.cards_uploaded}/{ev.cards_total}</span>
                        <Button size="sm" variant="ghost" className="text-xs text-zinc-400 hover:text-[#F5A623] h-6 px-2"
                          onClick={() => setCardEdit({ event_id: ev.event_id, total: ev.cards_total, uploaded: ev.cards_uploaded })}
                          data-testid="my-event-edit-cards">
                          Edit
                        </Button>
                      </div>
                    )}

                    {cardEdit?.event_id === ev.event_id && ev.cards_total > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        <Input type="number" min={0} value={cardEdit.total} onChange={e => setCardEdit({...cardEdit, total: parseInt(e.target.value) || 0})}
                          className="bg-[#0A0A0A] border-white/10 text-white h-8 w-24 text-sm" placeholder="Total" />
                        <Input type="number" min={0} value={cardEdit.uploaded} onChange={e => setCardEdit({...cardEdit, uploaded: parseInt(e.target.value) || 0})}
                          className="bg-[#0A0A0A] border-white/10 text-white h-8 w-24 text-sm" placeholder="Uploaded" />
                        <Button size="sm" className="bg-[#F5A623] text-black h-8"
                          onClick={() => { onUpdateCards(ev.project_id, ev.event_id, null, cardEdit.total, cardEdit.uploaded); setCardEdit(null); }}>
                          Save
                        </Button>
                        <Button size="sm" variant="outline" className="border-white/10 text-zinc-400 h-8" onClick={() => setCardEdit(null)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataManagementPage;
